/**
 * Carding Maintenance Form — Layer 2.
 *
 * Per-card-shift checklist + optional breakdown. Same structural shape
 * as the BR Maintenance page — only the card-specific items differ.
 * The breakdown sub-form, when filled, creates a MachineBreakdown row
 * that the production-log efficiency hook auto-picks up the next time
 * the operator saves a CARDING production log for the same machine /
 * shift / date.
 */
import { useState } from 'react'
import {
  App, Button, Card, Checkbox, Col, DatePicker, Form, Input, InputNumber,
  Row, Select, Space, Switch, Typography,
} from 'antd'
import { SaveOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import PageHeader from '@/shared/components/PageHeader'
import { filterActiveShifts } from '@/shared/constants/shifts'
import { plantsApi } from '@/features/masters/plants/api'
import { machinesApi } from '@/features/masters/machines/api'
import { shiftsApi } from '@/features/gate/api'
import { checklistLogsApi } from '@/features/baleManagement/api'

const { Text } = Typography

const CD_CHECKLIST: Array<{ key: string; label: string }> = [
  { key: 'safety_guards_ok',          label: 'Safety guards OK' },
  { key: 'emergency_stop_ok',         label: 'Emergency stop OK' },
  { key: 'abnormal_sound',            label: 'Abnormal sound (issue)' },
  { key: 'feed_plate_cleaned',        label: 'Feed plate cleaned' },
  { key: 'lickerin_cleaned',          label: 'Licker-in area cleaned' },
  { key: 'cylinder_cleaned',          label: 'Cylinder area cleaned' },
  { key: 'doffer_cleaned',            label: 'Doffer area cleaned' },
  { key: 'flats_cleaned',             label: 'Flats cleaned' },
  { key: 'suction_points_cleaned',    label: 'Suction points cleaned' },
  { key: 'coiler_cleaned',            label: 'Coiler cleaned' },
  { key: 'belts_checked',             label: 'Belts checked' },
  { key: 'bearings_checked',          label: 'Bearings checked' },
  { key: 'autoleveller_sensor_clean', label: 'Autoleveller sensor cleaned' },
  { key: 'can_table_checked',         label: 'Can table checked' },
  { key: 'card_clothing_ok',          label: 'Card clothing condition OK' },
  { key: 'wire_condition_ok',         label: 'Wire condition OK' },
  { key: 'gauge_setting_required',    label: 'Gauge setting required (issue)' },
  { key: 'stripping_required',        label: 'Stripping required (issue)' },
  { key: 'grinding_required',         label: 'Grinding required (issue)' },
]

const BREAKDOWN_CATEGORIES = [
  { value: 'M', label: 'M — Mechanical' },
  { value: 'E', label: 'E — Electrical' },
  { value: 'Q', label: 'Q — Quality Hold' },
  { value: 'P', label: 'P — Material Pending' },
  { value: 'U', label: 'U — Utility' },
  { value: 'C', label: 'C — Cleaning' },
  { value: 'S', label: 'S — Setup' },
]

export default function CardingMaintenancePage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [plantId, setPlantId] = useState<string | undefined>()
  const [machineId, setMachineId] = useState<string | undefined>()
  const [shiftId, setShiftId] = useState<string | undefined>()
  const [breakdownYes, setBreakdownYes] = useState(false)

  const { data: plantsData } = useQuery({
    queryKey: ['plants-all'],
    queryFn: () => plantsApi.list({ page: 1, page_size: 500 }),
  })
  const plantOptions = plantsData?.items?.map((p: any) => ({ label: p.name, value: p.id })) ?? []

  const { data: machinesData } = useQuery({
    queryKey: ['machines-cd-maint', plantId],
    queryFn: () => machinesApi.list({ plant_id: plantId, is_active: true, page: 1, page_size: 200 }),
    enabled: !!plantId,
  })
  const cardMachines = (machinesData?.items || []).filter((m: any) => {
    const t = (m.section || '').toUpperCase()
    return /^CARD/.test(t) || t === 'CARDING'
  })

  const { data: shifts = [] } = useQuery({
    queryKey: ['shifts-all-active'],
    queryFn: async () => filterActiveShifts(await shiftsApi.list()),
  })

  const saveMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => checklistLogsApi.upsert(body),
    onSuccess: (res: any) => {
      message.success(
        res.created
          ? 'Carding maintenance log saved'
          : 'Carding maintenance log updated for this shift'
      )
      qc.invalidateQueries({ queryKey: ['cd-maint'] })
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Save failed'),
  })

  const onSubmit = async () => {
    const v = await form.validateFields()
    if (!plantId || !machineId || !shiftId) {
      message.error('Plant, card, shift required')
      return
    }
    const items: Record<string, any> = {}
    CD_CHECKLIST.forEach(({ key }) => { items[key] = !!v[key] })
    items.breakdown_yes = !!breakdownYes

    const breakdown = breakdownYes ? {
      breakdown_time: v.breakdown_time?.toISOString(),
      restoration_time: v.restoration_time?.toISOString(),
      duration_minutes: v.duration_minutes ?? null,
      category: v.breakdown_category || 'M',
      description: v.breakdown_reason,
      action_taken: v.action_taken,
      spare_used: v.spare_used,
    } : undefined

    saveMutation.mutate({
      plant_id: plantId,
      log_date: dayjs().format('YYYY-MM-DD'),
      entry_type: 'CARD_MAINT',
      shift_id: shiftId,
      machine_id: machineId,
      items_json: items,
      remarks: v.remarks || null,
      breakdown,
    })
  }

  return (
    <div>
      <PageHeader
        title="Carding — Maintenance"
        subtitle="Per-card daily checklist. Breakdowns logged here auto-feed efficiency on the matching production log."
      />
      <Card>
        <Form layout="vertical" form={form} size="large">
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item label="Plant" required>
                <Select options={plantOptions} value={plantId} onChange={setPlantId} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Card Machine" required>
                <Select
                  options={cardMachines.map((m: any) => ({
                    label: `${m.machine_id_str} — ${m.name}`, value: m.id,
                  }))}
                  value={machineId} onChange={setMachineId}
                  placeholder={cardMachines.length ? 'Select card' : 'Add cards in Masters'}
                  disabled={!plantId} showSearch optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Shift" required>
                <Select
                  options={(shifts || []).map((s: any) => ({
                    label: s.shift_name || s.shift_code, value: s.id,
                  }))}
                  value={shiftId} onChange={setShiftId}
                />
              </Form.Item>
            </Col>
          </Row>

          <Card type="inner" title="Daily checklist — tick what's OK / done">
            <Row gutter={[12, 8]}>
              {CD_CHECKLIST.map(({ key, label }) => (
                <Col xs={24} sm={12} md={8} key={key}>
                  <Form.Item name={key} valuePropName="checked" style={{ marginBottom: 4 }}>
                    <Checkbox>{label}</Checkbox>
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </Card>

          <Card
            type="inner" title={
              <Space>
                <span>Breakdown</span>
                <Switch
                  checked={breakdownYes}
                  onChange={setBreakdownYes}
                  checkedChildren="YES" unCheckedChildren="NO"
                />
              </Space>
            }
            style={{ marginTop: 12 }}
          >
            {!breakdownYes ? (
              <Text type="secondary">No breakdown reported. Skip this section.</Text>
            ) : (
              <>
                <Row gutter={12}>
                  <Col xs={24} md={8}>
                    <Form.Item name="breakdown_time" label="Start time">
                      <DatePicker showTime style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="restoration_time" label="End time">
                      <DatePicker showTime style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="duration_minutes" label="Duration (min) — optional">
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col xs={24} md={8}>
                    <Form.Item name="breakdown_category" label="Category" initialValue="M">
                      <Select options={BREAKDOWN_CATEGORIES} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={16}>
                    <Form.Item name="breakdown_reason" label="Reason / description">
                      <Input placeholder="e.g. Doffer wire damage" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col xs={24} md={12}>
                    <Form.Item name="action_taken" label="Action taken">
                      <Input.TextArea rows={2} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="spare_used" label="Spare used (if any)">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
          </Card>

          <Form.Item name="remarks" label="Remarks" style={{ marginTop: 12 }}>
            <Input.TextArea rows={2} maxLength={300} />
          </Form.Item>

          <Button
            type="primary" size="large" block icon={<SaveOutlined />}
            loading={saveMutation.isPending} onClick={onSubmit}
          >
            Save Carding Maintenance Log
          </Button>
        </Form>
      </Card>
    </div>
  )
}
